// Heavily Copilot Assisted
//
// This mixin provides keyboard accessibility for the MarkupView.
// It enables keyboard-driven mark selection, placement, navigation, and manipulation
// for all supported mark types (rectangle, circle, text, bracket, drawn line).
//
// Key features:
// - Tool arming (C, R, T, B)
// - Mark placement (Enter)
// - Keyboard navigation and manipulation (arrows, +/-, PgUp/PgDn, Tab, Delete)
// - Region focus toggle (F2)
// - Escape to unarm/reset controls
// - Synchronization with Konva canvas and Vue state

export default {
  mounted() {
    // Attach global keydown handler when component is mounted
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeUnmount() {
    // Remove handler when component is destroyed
    window.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    /**
     * Main keyboard event handler for all accessibility features.
     * Handles tool arming, mark placement, navigation, manipulation, and region focus.
     */
    handleKeydown(e) {
      // Only act if not in an input or textarea (don't interfere with typing)
      const tag = document.activeElement.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea") return;

      // Ignore if focus is inside a dropdown menu (e.g., color picker)
      if (
        document.activeElement.closest &&
        document.activeElement.closest(".dropdown-menu")
      ) {
        return;
      }

      // --- GLOBAL SHORTCUTS ---

      // Escape: reset/unarm controls (return to neutral state)
      if (e.key === "Escape") {
        this.isPlacingMark = false;
        this.selectedMarkType = "";
        e.preventDefault();
        return;
      }

      // --- TOOL ARMING ---

      // If not currently arming a mark, allow tool selection by key
      if (!this.isPlacingMark) {
        if (e.key === "c" || e.key === "C") {
          this.changeMarkType("circle");
          e.preventDefault();
        } else if (e.key === "r" || e.key === "R") {
          this.changeMarkType("rectangle");
          e.preventDefault();
        } else if (e.key === "t" || e.key === "T") {
          this.changeMarkType("text");
          e.preventDefault();
        } else if (e.key === "b" || e.key === "B") {
          this.changeMarkType("bracket");
          e.preventDefault();
        }
      }

      // --- MARK PLACEMENT ---

      // Place mark if armed and Enter is pressed (places at canvas center)
      if (this.isPlacingMark && e.key === "Enter") {
        // Simulate a click in the center of the canvas
        const stage = this.$refs.stage.getNode();
        const pos = { x: this.clientWidth / 2, y: this.clientWidth / 2 };
        const evt = {
          target: stage,
          getStage: () => stage,
          // Simulate Konva pointer position
          ...{
            target: {
              getStage: () => stage
            }
          }
        };
        // Patch getPointerPosition for addMark
        stage.getPointerPosition = () => pos;
        this.addMark(evt);
        e.preventDefault();
      }

      // --- MARK NAVIGATION & MANIPULATION ---

      // If a mark is selected, allow manipulation and navigation
      if (this.selectedShapeName) {
        // Find the correct mark array and mark object
        let markArr, mark;
        if (this.selectedShapeName.startsWith("rect")) {
          markArr = this.rectangles;
        } else if (this.selectedShapeName.startsWith("circ")) {
          markArr = this.circles;
        } else if (this.selectedShapeName.startsWith("text")) {
          markArr = this.texts;
        } else if (this.selectedShapeName.startsWith("bracket")) {
          markArr = this.brackets;
        } else if (this.selectedShapeName.startsWith("draw")) {
          markArr = this.drawn;
        } else {
          return;
        }
        mark = markArr.find((m) => m.name === this.selectedShapeName);
        if (!mark) return;

        // Delete key: remove selected mark (support both Delete and Backspace)
        if (e.key === "Delete" || e.key === "Backspace") {
          this.deleteMark();
          e.preventDefault();
          return;
        }

        // Arrow keys: move mark (shift for larger steps)
        const moveStep = e.shiftKey ? 10 : 2;
        if (
          ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)
        ) {
          if (e.key === "ArrowLeft") mark.x -= moveStep;
          if (e.key === "ArrowRight") mark.x += moveStep;
          if (e.key === "ArrowUp") mark.y -= moveStep;
          if (e.key === "ArrowDown") mark.y += moveStep;
          this.syncKonvaNode();
          e.preventDefault();
        }

        // [ / ] : scale mark down/up
        if (e.key === "]") {
          mark.scaleX = (mark.scaleX || 1) * 1.1;
          mark.scaleY = (mark.scaleY || 1) * 1.1;
          this.syncKonvaNode();
          e.preventDefault();
        }
        if (e.key === "[") {
          mark.scaleX = (mark.scaleX || 1) / 1.1;
          mark.scaleY = (mark.scaleY || 1) / 1.1;
          this.syncKonvaNode();
          e.preventDefault();
        }

        // PageUp/PageDown: rotate mark
        if (e.key === "PageUp") {
          mark.rotation = (mark.rotation || 0) + 5;
          this.syncKonvaNode();
          e.preventDefault();
        }
        if (e.key === "PageDown") {
          mark.rotation = (mark.rotation || 0) - 5;
          this.syncKonvaNode();
          e.preventDefault();
        }

        // Tab: cycle selection through all marks (shift+Tab for reverse)
        if (e.key === "Tab") {
          const allMarks = [
            ...this.rectangles,
            ...this.circles,
            ...this.texts,
            ...this.brackets,
            ...this.drawn // Include drawn lines in tab order
          ];
          const idx = allMarks.findIndex(
            (m) => m.name === this.selectedShapeName
          );
          let nextIdx = e.shiftKey ? idx - 1 : idx + 1;
          if (nextIdx < 0) nextIdx = allMarks.length - 1;
          if (nextIdx >= allMarks.length) nextIdx = 0;
          if (allMarks[nextIdx]) {
            this.selectedShapeName = allMarks[nextIdx].name;
            this.updateTransformer();
          }
          e.preventDefault();
        }

        // Always update transformer after keyboard manipulation
        this.updateTransformer();
      }

      // --- REGION FOCUS TOGGLE (TOOLS/CANVAS) ---

      // Tilde/grave (`) toggles between DOM (tools) and Konva/canvas marks
      if (e.key === "`") {
        const allMarks = [
          ...this.rectangles,
          ...this.circles,
          ...this.texts,
          ...this.brackets,
          ...this.drawn
        ];
        if (!this.selectedShapeName) {
          // Only toggle to canvas if there are marks to select
          if (allMarks.length > 0) {
            if (typeof this.setActiveRegion === "function") {
              this.setActiveRegion(true); // highlight stage
            }
            this.selectedShapeName = allMarks[0].name;
            this.updateTransformer();
            e.preventDefault();
            return;
          } else {
            // No marks: keep focus in DOM/tools, do not toggle canvasActive
            if (typeof this.setActiveRegion === "function") {
              this.setActiveRegion(false);
            }
            e.preventDefault();
            return;
          }
        } else {
          // Mark is selected: clear selection, return to DOM tab order
          this.selectedShapeName = "";
          this.updateTransformer();
          if (typeof this.setActiveRegion === "function") {
            this.setActiveRegion(false); // highlight tools
          }
          e.preventDefault();
          return;
        }
      }
    },

    /**
     * Ensures the Konva node is updated after keyboard-driven changes.
     * This keeps the visual state in sync with the mark object.
     */
    syncKonvaNode() {
      if (!this.$refs.stage || !this.selectedShapeName) return;
      const stage = this.$refs.stage.getNode();
      const node = stage.findOne("." + this.selectedShapeName);
      if (node) {
        node.getLayer().batchDraw();
      }
    }
  }
};
